import React, { useState, useMemo, useEffect, useCallback } from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
	Box,
	Chip,
	Paper,
	Popover,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
} from "@mui/material";
import debounce from "lodash/debounce";

const escapeRegExp = (string) => {
	return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
};

const CustomerDataTable = ({ customers }) => {
	const [pageSize, setPageSize] = useState(10);
	const [searchText, setSearchText] = useState("");
	const [filteredCustomers, setFilteredCustomers] = useState(customers);
	const [popoverAnchor, setPopoverAnchor] = useState(null);
	const [popoverContent, setPopoverContent] = useState(null);

	const handlePopoverOpen = (event, representatives) => {
		setPopoverAnchor(event.currentTarget);
		setPopoverContent(representatives);
	};

	const handlePopoverClose = () => {
		setPopoverAnchor(null);
		setPopoverContent(null);
	};

	const debouncedFilterCustomers = useCallback(
		debounce((searchValue) => {
			const sanitizedValue = escapeRegExp(searchValue.trim().toLowerCase());
			const regex = new RegExp(sanitizedValue, "i");
			const filteredData = customers.filter(
				(customer) =>
					regex.test(customer.customerName) ||
					regex.test(customer.customerEmail) ||
					regex.test(customer.customerNumber) ||
					customer.representatives.some(
						(rep) =>
							regex.test(rep.name) ||
							regex.test(rep.phone) ||
							regex.test(rep.email) ||
							regex.test(rep.role)
					)
			);
			setFilteredCustomers(filteredData);
		}, 300),
		[customers]
	);

	const handleSearchChange = (event) => {
		const value = event.target.value;
		setSearchText(value);
		debouncedFilterCustomers(value);
	};

	useEffect(() => {
		setFilteredCustomers(customers);
	}, [customers]);

	const open = Boolean(popoverAnchor);

	const columns = useMemo(
		() => [
			{
				field: "_id",
				headerName: "ID",
				flex: 0.05,
				renderCell: (params) => (
					<Tooltip title={params.value}>
						<span>{params.value}</span>
					</Tooltip>
				),
			},
			{
				field: "customerType",
				headerName: "Type | MCNumber",
				flex: 0.15,
				renderCell: (params) => {
					return (
						<div>
							<div>
								{params.value}

								{params.value === "broker" && params.row.mcNumber.length > 0 ? (
									<Chip sx={{ ml: 1, borderRadius: 1 }} size="small" label={params.row.mcNumber} />
								) : (
									""
								)}
							</div>
						</div>
					);
				},
			},
			{
				field: "customerName",
				headerName: "Name",
				flex: 0.2,
				renderCell: (params) => (
					<Tooltip title={params.value}>
						<span>{params.value}</span>
					</Tooltip>
				),
			},
			{
				field: "customerNumber",
				headerName: "Number",
				flex: 0.2,
				renderCell: (params) => (
					<Tooltip title={params.value}>
						<span>{params.value}</span>
					</Tooltip>
				),
			},
			{
				field: "customerEmail",
				headerName: "Email",
				flex: 0.3,
				filterable: true,
				renderCell: (params) => (
					<Tooltip title={params.value}>
						<span>{params.value}</span>
					</Tooltip>
				),
			},
			{
				field: "representatives",
				headerName: "Representatives",
				flex: 0.1,
				renderCell: (params) => (
					<Box>
						{params.value.length > 0 && (
							<>
								<Chip label="See All" onClick={(event) => handlePopoverOpen(event, params.value)} />
							</>
						)}
					</Box>
				),
			},
		],
		[]
	);

	return (
		<div style={{ height: 700, width: "100%" }}>
			<Box mb={2} sx={{ width: "260px", marginLeft: "auto" }}>
				<TextField
					fullWidth
					label="Search"
					variant="outlined"
					size="small"
					value={searchText}
					onChange={handleSearchChange}
				/>
			</Box>
			<DataGrid
				rows={filteredCustomers.map((customer) => ({ ...customer, id: customer._id }))}
				columns={columns}
				pageSize={pageSize}
				rowsPerPageOptions={[10, 20, 50, 100]}
				onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
				pagination
				autoHeight
				disableSelectionOnClick
				getRowId={(row) => row._id}
				components={{
					NoRowsOverlay: () => (
						<div style={{ padding: "20px", textAlign: "center" }}>No customers found.</div>
					),
				}}
			/>
			<Popover
				open={open}
				anchorEl={popoverAnchor}
				onClose={handlePopoverClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<Box p={2}>
					{popoverContent && (
						<TableContainer component={Paper}>
							<Table size="small" aria-label="representatives table">
								<TableHead>
									<TableRow>
										<TableCell>Name</TableCell>
										<TableCell>Phone</TableCell>
										<TableCell>Email</TableCell>
										<TableCell>Role</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{popoverContent.map((rep, index) => (
										<TableRow key={index}>
											<TableCell>{rep.name}</TableCell>
											<TableCell>{rep.phone}</TableCell>
											<TableCell>{rep.email}</TableCell>
											<TableCell>{rep.role}</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</Box>
			</Popover>
		</div>
	);
};

export default CustomerDataTable;
